import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useAutogestionEntradas } from '../../hooks/useAutogestionEntradas';
import { MisEntradasView } from './MisEntradasView';
import { ModalNominacion } from '../../components/ModalNominacion';
import { GenerarLinkModal } from '../../components/GenerarLinkModal';
import { EntradaDigitalModal } from '../../components/EntradaDigitalModal';
import { EntradaAfiliadoDto } from '../../types';
import congresoService from '../../services/congresoService';
import autogestionService from '../../services/autogestionService';
import { useGlobalState } from '../../../../state';

export const MisEntradas: React.FC = () => {
  const [user] = useGlobalState('user');
  const idAfiliadoLogueado = user?.datosUsuario?.idAfiliado || user?.datosUsuario?.legajoPrevisional || 12345;

  const {
    entradas,
    cargando,
    error,
    cargarEntradas,
    nominarEntrada,
    reasignarEntrada,
    descargarCertificado
  } = useAutogestionEntradas();

  const [entradaSeleccionada, setEntradaSeleccionada] = useState<EntradaAfiliadoDto | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [ventaOnlineHabilitada, setVentaOnlineHabilitada] = useState(false);
  const [cargandoConfig, setCargandoConfig] = useState(false);
  const [eventoNombre, setEventoNombre] = useState('Congreso de Martilleros y Corredores Públicos');

  const [entradaDigitalModalOpen, setEntradaDigitalModalOpen] = useState(false);
  const [entradaDigitalSeleccionada, setEntradaDigitalSeleccionada] = useState<EntradaAfiliadoDto | null>(null);
  const [descargandoPdf, setDescargandoPdf] = useState(false);

  useEffect(() => {
    cargarEntradas(idAfiliadoLogueado);
    
    const verificarConfigVenta = async () => {
      setCargandoConfig(true);
      try {
        const congresos = await congresoService.getCongresosActivos();
        if (congresos && congresos.length > 0) {
          const activo = congresos[0];
          setEventoNombre(activo.nombre);
          
          if (activo.configuracionesEntradas && activo.configuracionesEntradas.length > 0) {
            const habilitada = activo.configuracionesEntradas.some((c: any) => c.ventaOnlineHabilitada);
            setVentaOnlineHabilitada(habilitada);
          }
        }
      } catch (err) {
        console.error('Error al cargar config de congresos:', err);
      } finally {
        setCargandoConfig(false);
      }
    };

    verificarConfigVenta();
  }, [cargarEntradas, idAfiliadoLogueado]);

  const handleNominarClick = (entrada: EntradaAfiliadoDto) => {
    setEntradaSeleccionada(entrada);
    setModalOpen(true);
  };

  const handleSaveNominacion = async (
    idEntradaDetalle: number, 
    nombre: string, 
    apellido: string, 
    dni: string, 
    email: string
  ) => {
    try {
      if (entradaSeleccionada?.nominada) {
        await reasignarEntrada(idEntradaDetalle, nombre, apellido, dni, email);
        toast.success('Portador reasignado exitosamente.');
      } else {
        await nominarEntrada(idEntradaDetalle, nombre, apellido, dni, email);
        toast.success('Entrada nominada exitosamente.');
      }
      cargarEntradas(idAfiliadoLogueado);
    } catch (err: any) {
      toast.error(err?.mensaje || 'Error al guardar nominación.');
      throw err;
    }
  };

  const handleDescargarCertificado = async (idEntradaDetalle: number) => {
    try {
      const result = await descargarCertificado(idEntradaDetalle);
      if (result.generadoExitosamente) {
        toast.success(`Certificado emitido para ${result.asistenteNombre}`);
      } else {
        toast.error(`No se pudo emitir: ${result.mensajeError || 'Asistencia incompleta.'}`);
      }
    } catch (err: any) {
      toast.error(err?.mensaje || 'Error al emitir certificado.');
    }
  };

  const handleVerEntradaDigital = (entrada: EntradaAfiliadoDto) => {
    setEntradaDigitalSeleccionada(entrada);
    setEntradaDigitalModalOpen(true);
  };

  const handleDescargarPdf = async (idEntradaDetalle: number) => {
    try {
      setDescargandoPdf(true);
      const blob = await congresoService.descargarTicketPdf(idEntradaDetalle, idAfiliadoLogueado);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Ticket-Entrada-${idEntradaDetalle}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Ticket PDF descargado exitosamente.');
    } catch (err: any) {
      console.error('Error al descargar ticket PDF:', err);
      toast.error(err?.mensaje || 'Error al descargar el ticket PDF.');
    } finally {
      setDescargandoPdf(false);
    }
  };

  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkTransferenciaInfo, setLinkTransferenciaInfo] = useState<{ numeroEntrada: string; urlTransferencia: string; fechaExpiracion?: string } | null>(null);

  const handleGenerarLinkClick = async (entrada: EntradaAfiliadoDto) => {
    try {
      const res = await autogestionService.generarLinkTransferencia(entrada.idEntradaDetalle, idAfiliadoLogueado);
      if (res.exitoso && res.token) {
        const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
        const publicUrl = res.urlTransferencia || `${apiUrl}/congresos/entradas/transferencia/${res.token}`;
        setLinkTransferenciaInfo({
          numeroEntrada: entrada.numeroEntrada,
          urlTransferencia: publicUrl,
          fechaExpiracion: res.fechaExpiracion
        });
        setLinkModalOpen(true);
      } else {
        toast.error(res.mensaje || 'Error al generar enlace.');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Error al generar enlace de transferencia.');
    }
  };

  const handleAdquirirClick = () => {
    toast('Las entradas se adquieren a través de su Colegio Departamental o presencialmente.', {
      icon: '🏛️'
    });
  };

  return (
    <>
      <MisEntradasView
        entradas={entradas}
        cargando={cargando}
        cargandoConfig={cargandoConfig}
        error={error}
        ventaOnlineHabilitada={ventaOnlineHabilitada}
        eventoNombre={eventoNombre}
        onAdquirirClick={handleAdquirirClick}
        onNominarClick={handleNominarClick}
        onDescargarCertificadoClick={handleDescargarCertificado}
        onGenerarLinkClick={handleGenerarLinkClick}
        onVerEntradaDigitalClick={handleVerEntradaDigital}
        onDescargarPdfClick={handleDescargarPdf}
      />
      
      <ModalNominacion
        entrada={entradaSeleccionada}
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEntradaSeleccionada(null);
        }}
        onSave={handleSaveNominacion}
        cargando={cargando}
      />

      {linkTransferenciaInfo && (
        <GenerarLinkModal
          isOpen={linkModalOpen}
          onClose={() => setLinkModalOpen(false)}
          numeroEntrada={linkTransferenciaInfo.numeroEntrada}
          urlTransferencia={linkTransferenciaInfo.urlTransferencia}
          fechaExpiracion={linkTransferenciaInfo.fechaExpiracion}
        />
      )}

      <EntradaDigitalModal
        isOpen={entradaDigitalModalOpen}
        onClose={() => {
          setEntradaDigitalModalOpen(false);
          setEntradaDigitalSeleccionada(null);
        }}
        entrada={entradaDigitalSeleccionada}
        idAfiliado={idAfiliadoLogueado}
        onDescargarPdf={handleDescargarPdf}
        descargandoPdf={descargandoPdf}
      />
    </>
  );
};
